// Create or rename a matter — a named sub-group of a project's work that can
// go out on its own invoice (migration 0061). Used from the project page and
// from <MatterSelect>'s "+ New matter…" option, where it nests inside another
// form's modal; <Modal> portals it and stops its submit bubbling, so saving
// here never submits the form underneath.

import { useState, type FormEvent } from 'react';
import type { ProjectMatter } from '@stfors/shared-types';
import { Modal } from './Modal';
import { useCreateMatter, useUpdateMatter } from '../api/queries/matters';
import { apiErrorText } from '../utils/apiErrors';
import './MatterFormModal.css';

export interface MatterFormModalProps {
  projectId: number;
  /** Rename this matter; omit to create one. */
  matter?: ProjectMatter;
  onClose: () => void;
  onSaved?: (matter: ProjectMatter) => void;
}

export function MatterFormModal({ projectId, matter, onClose, onSaved }: MatterFormModalProps): JSX.Element {
  const create = useCreateMatter();
  const update = useUpdateMatter();
  const [name, setName] = useState(matter?.name ?? '');
  const [description, setDescription] = useState(matter?.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const busy = create.isPending || update.isPending;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim() === '') return setError('Give the matter a name.');
    try {
      const desc = description.trim() === '' ? null : description.trim();
      const { matter: saved } = matter
        ? await update.mutateAsync({ id: matter.id, name: name.trim(), description: desc })
        : await create.mutateAsync({ project_id: projectId, name: name.trim(), description: desc });
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(apiErrorText(err, matter ? 'Could not rename the matter.' : 'Could not add the matter.'));
    }
  };

  return (
    <Modal
      title={matter ? 'Edit Matter' : 'New Matter'}
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn-charcoal-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" form="matter-form" className="btn-cyan-primary" disabled={busy}>
            {busy ? 'Saving…' : matter ? 'Save' : 'Add matter'}
          </button>
        </>
      }
    >
      <form id="matter-form" className="mfm-form" onSubmit={onSubmit} noValidate>
        <p className="mfm-lead">
          One job under this project. Its time and expenses can go out on their own invoice;
          rates and the bill-to still come from the project.
        </p>
        <label className="mfm-field">
          <span>Name *</span>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            disabled={busy}
            placeholder="e.g. Sylvetsky iPhone"
            autoFocus
          />
        </label>
        <label className="mfm-field">
          <span>Description</span>
          <textarea
            className="form-input"
            rows={2}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy}
          />
        </label>
        {error && <p className="mfm-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
}
